'use client'

import { forwardRef, useState, useEffect, useRef, useCallback } from 'react'
import { Search, X, Clock, TrendingUp, Users, Calendar, DollarSign, RotateCcw } from 'lucide-react'
import { safeAccess } from '../utils/security'

/**
 * BIRHAUS Search Input Component
 * 
 * Enhanced search input implementing all 10 BIRHAUS principles:
 * - Spanish-first placeholders and suggestions
 * - Progressive disclosure of search results (max 7 per Miller's Law)
 * - Auto-complete with cognitive load reduction
 * - Recent searches history (max 5)
 * - Accessibility compliance (WCAG AA+)
 * - Undo functionality for search history changes
 * - Performance optimized with debouncing
 * - IPU Admin specific search categories
 */

// BIRHAUS: Utility function (self-contained)
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

export interface SearchSuggestion {
  id: string
  text: string
  category?: SearchCategory
  icon?: React.ReactNode
  metadata?: string
}

export interface SearchCategory {
  id: string
  labelEs: string
  labelEn: string
  icon: React.ReactNode
  color: string
}

interface UndoState {
  previousHistory: string[]
  timeout: NodeJS.Timeout | null
  isActive: boolean
}

export interface BirhausSearchInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  // BIRHAUS Principle #7: Bilingual by design
  label?: string
  labelEs?: string
  labelEn?: string
  
  // Placeholder (Spanish-first)
  placeholderEs?: string
  placeholderEn?: string
  
  // Search functionality
  value?: string
  defaultValue?: string
  onSearch?: (query: string) => void
  onSuggestionSelect?: (suggestion: SearchSuggestion) => void
  
  // Auto-complete
  suggestions?: SearchSuggestion[]
  loadingSuggestions?: boolean
  maxSuggestions?: number // Default 7 (Miller's Law)
  
  // Search categories (IPU Admin)
  categories?: SearchCategory[]
  selectedCategory?: string
  onCategoryChange?: (categoryId: string) => void
  
  // Recent searches
  enableRecentSearches?: boolean
  maxRecentSearches?: number // Default 5
  recentSearches?: string[]
  onRecentSearchesChange?: (searches: string[]) => void
  
  // BIRHAUS Principle #5: Undo over confirm
  enableUndo?: boolean
  undoTimeoutMs?: number
  
  // BIRHAUS Principle #8: Performance is UX
  debounceMs?: number
  minQueryLength?: number
  
  // BIRHAUS Principle #6: Accessibility = dignity
  disabled?: boolean
  required?: boolean
  
  // Appearance
  size?: 'sm' | 'md' | 'lg'
  variant?: 'default' | 'minimal' | 'filled'
  showIcon?: boolean
  
  // Locale
  locale?: 'es' | 'en'
  
  // IPU Admin specific
  searchScope?: 'all' | 'members' | 'donations' | 'events' | 'reports'
}

export const BirhausSearchInput = forwardRef<HTMLInputElement, BirhausSearchInputProps>(({
  // Labels (Spanish-first)
  label,
  labelEs,
  labelEn,
  
  // Placeholders
  placeholder,
  placeholderEs,
  placeholderEn,
  
  // Value
  value,
  defaultValue,
  onChange,
  onSearch,
  onSuggestionSelect,
  
  // Auto-complete
  suggestions = [],
  loadingSuggestions = false,
  maxSuggestions = 7,
  
  // Categories
  categories,
  selectedCategory,
  onCategoryChange,
  
  // Recent searches
  enableRecentSearches = true,
  maxRecentSearches = 5,
  recentSearches = [],
  onRecentSearchesChange,
  
  // Undo
  enableUndo = true,
  undoTimeoutMs = 10000,
  
  // Performance
  debounceMs = 300,
  minQueryLength = 2,
  
  // Basic props
  disabled = false,
  required = false,
  
  // Appearance
  size = 'md',
  variant = 'default',
  showIcon = true,
  
  // Locale
  locale = 'es',
  
  // IPU Admin
  searchScope = 'all',
  
  // Standard props
  ...props
}, ref) => {
  // State management
  const [internalValue, setInternalValue] = useState<string>(value || defaultValue || '')
  const [isFocused, setIsFocused] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const [internalRecentSearches, setInternalRecentSearches] = useState<string[]>(recentSearches)
  const [undoState, setUndoState] = useState<UndoState>({
    previousHistory: [],
    timeout: null,
    isActive: false
  })
  
  // Refs
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const debounceTimeoutRef = useRef<NodeJS.Timeout>()
  
  // BIRHAUS Principle #7: Spanish-first text resolution
  const getDisplayText = useCallback((
    primary: string | undefined,
    spanish: string | undefined,
    english: string | undefined,
    fallback: string
  ): string => {
    if (locale === 'es') {
      return primary || spanish || english || fallback
    }
    return primary || english || spanish || fallback
  }, [locale])
  
  const displayLabel = getDisplayText(label, labelEs, labelEn, '')
  const displayPlaceholder = getDisplayText(
    placeholder,
    placeholderEs,
    placeholderEn,
    locale === 'es' ? 'Buscar...' : 'Search...'
  )
  
  // Default categories for IPU Admin
  const getDefaultCategories = useCallback((): SearchCategory[] => [
    {
      id: 'members',
      labelEs: 'Miembros',
      labelEn: 'Members',
      icon: <Users className="h-4 w-4" />,
      color: 'blue'
    },
    {
      id: 'donations',
      labelEs: 'Donaciones',
      labelEn: 'Donations',
      icon: <DollarSign className="h-4 w-4" />,
      color: 'green'
    },
    {
      id: 'events',
      labelEs: 'Eventos',
      labelEn: 'Events',
      icon: <Calendar className="h-4 w-4" />,
      color: 'purple'
    }
  ], [])
  
  const allCategories = categories || getDefaultCategories()
  
  // Filter suggestions based on query and limits
  const filteredSuggestions = useCallback(() => {
    if (!internalValue || internalValue.length < minQueryLength) {
      return []
    }
    
    let filtered = suggestions.filter(suggestion =>
      suggestion.text.toLowerCase().includes(internalValue.toLowerCase())
    )
    
    // Filter by category if selected
    if (selectedCategory) {
      filtered = filtered.filter(suggestion => 
        suggestion.category?.id === selectedCategory
      )
    }
    
    return filtered.slice(0, maxSuggestions)
  }, [suggestions, internalValue, minQueryLength, selectedCategory, maxSuggestions])
  
  // Handle undo functionality
  const handleUndo = useCallback(() => {
    if (undoState.isActive && undoState.timeout) {
      clearTimeout(undoState.timeout)
      setInternalRecentSearches(undoState.previousHistory)
      onRecentSearchesChange?.(undoState.previousHistory)
      setUndoState({ previousHistory: [], timeout: null, isActive: false })
    }
  }, [undoState, onRecentSearchesChange])
  
  // Setup undo for search history changes
  const setupUndo = useCallback((newHistory: string[], oldHistory: string[]) => {
    if (!enableUndo || newHistory.length === oldHistory.length) return
    
    // Clear existing timeout
    if (undoState.timeout) {
      clearTimeout(undoState.timeout)
    }
    
    // Set up new undo state
    const timeout = setTimeout(() => {
      setUndoState(prev => ({ ...prev, isActive: false, timeout: null }))
    }, undoTimeoutMs)
    
    setUndoState({
      previousHistory: oldHistory,
      timeout,
      isActive: true
    })
  }, [enableUndo, undoTimeoutMs, undoState.timeout])
  
  // Add to recent searches
  const addToRecentSearches = useCallback((query: string) => {
    if (!query.trim() || !enableRecentSearches) return
    
    const oldHistory = [...internalRecentSearches]
    const newHistory = [query, ...oldHistory.filter(item => item !== query)]
      .slice(0, maxRecentSearches)
    
    setInternalRecentSearches(newHistory)
    onRecentSearchesChange?.(newHistory)
    
    // Setup undo
    setupUndo(newHistory, oldHistory)
  }, [
    internalRecentSearches,
    enableRecentSearches,
    maxRecentSearches,
    onRecentSearchesChange,
    setupUndo
  ])
  
  // Clear recent searches
  const clearRecentSearches = useCallback(() => {
    const oldHistory = [...internalRecentSearches]
    setInternalRecentSearches([])
    onRecentSearchesChange?.([])
    setupUndo([], oldHistory)
  }, [internalRecentSearches, onRecentSearchesChange, setupUndo])
  
  // Handle input change with debouncing
  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value
    setInternalValue(newValue)
    setShowDropdown(true)
    
    // Clear existing debounce
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
    }
    
    // Debounced search callback
    debounceTimeoutRef.current = setTimeout(() => {
      if (newValue.length >= minQueryLength) {
        onSearch?.(newValue)
      }
    }, debounceMs)
    
    // Call parent onChange
    onChange?.(event)
  }, [onChange, onSearch, debounceMs, minQueryLength])
  
  // Handle search submission
  const handleSubmit = useCallback((query?: string) => {
    const searchQuery = query || internalValue
    if (searchQuery.trim()) {
      addToRecentSearches(searchQuery.trim())
      onSearch?.(searchQuery.trim())
      setShowDropdown(false)
    }
  }, [internalValue, addToRecentSearches, onSearch])
  
  // Handle suggestion selection
  const handleSuggestionSelect = useCallback((suggestion: SearchSuggestion) => {
    setInternalValue(suggestion.text)
    addToRecentSearches(suggestion.text)
    onSuggestionSelect?.(suggestion)
    setShowDropdown(false)
    
    // Focus back to input
    inputRef.current?.focus()
  }, [addToRecentSearches, onSuggestionSelect])
  
  // Handle recent search selection
  const handleRecentSearchSelect = useCallback((query: string) => {
    setInternalValue(query)
    handleSubmit(query)
  }, [handleSubmit])
  
  // Handle clear
  const handleClear = useCallback(() => {
    setInternalValue('')
    setShowDropdown(false)
    inputRef.current?.focus()
    onChange?.({ target: { value: '' } } as React.ChangeEvent<HTMLInputElement>)
  }, [onChange])
  
  // Handle focus
  const handleFocus = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true)
    setShowDropdown(true)
    props.onFocus?.(event)
  }, [props])
  
  // Handle blur
  const handleBlur = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false)
    // Delay hiding dropdown to allow clicks on suggestions
    setTimeout(() => setShowDropdown(false), 150)
    props.onBlur?.(event)
  }, [props])
  
  // Handle key navigation
  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      handleSubmit()
    } else if (event.key === 'Escape') {
      setShowDropdown(false)
      inputRef.current?.blur()
    }
    
    props.onKeyDown?.(event)
  }, [handleSubmit, props])
  
  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        dropdownRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false)
      }
    }
    
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showDropdown])
  
  // Sync external value changes
  useEffect(() => {
    if (value !== undefined && value !== internalValue) {
      setInternalValue(value)
    }
  }, [value, internalValue])
  
  // Sync external recent searches
  useEffect(() => {
    if (recentSearches !== internalRecentSearches) {
      setInternalRecentSearches(recentSearches)
    }
  }, [recentSearches, internalRecentSearches])
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
      if (undoState.timeout) {
        clearTimeout(undoState.timeout)
      }
    }
  }, [undoState.timeout])
  
  // Style classes
  const sizeClasses = {
    sm: 'text-sm px-3 py-1.5',
    md: 'text-base px-4 py-2',
    lg: 'text-lg px-5 py-3'
  }
  
  const variantClasses = {
    default: 'bg-white border border-gray-300',
    filled: 'bg-gray-50 border border-gray-200',
    minimal: 'bg-transparent border-b border-gray-300'
  }
  
  const iconSizes = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6'
  }
  
  // Calculate if we should show suggestions or recent searches
  const shouldShowSuggestions = filteredSuggestions().length > 0
  const shouldShowRecentSearches = !shouldShowSuggestions && 
    internalRecentSearches.length > 0 && 
    internalValue.length < minQueryLength
  
  return (
    <div className="birhaus-search-input">
      {/* Label */}
      {displayLabel && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {displayLabel}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      {/* Categories Filter */}
      {allCategories.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          <button
            type="button"
            onClick={() => onCategoryChange?.('')}
            className={cn(
              'px-3 py-1 text-xs rounded-full transition-colors',
              !selectedCategory
                ? 'bg-primary-100 text-primary-700 border border-primary-200'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            )}
          >
            {locale === 'es' ? 'Todas' : 'All'}
          </button>
          {allCategories.map((category) => (
            <button
              key={category.id}
              type="button"
              onClick={() => onCategoryChange?.(category.id)}
              className={cn(
                'px-3 py-1 text-xs rounded-full transition-colors flex items-center space-x-1',
                selectedCategory === category.id
                  ? `bg-${category.color}-100 text-${category.color}-700 border border-${category.color}-200`
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              )}
            >
              {category.icon}
              <span>{locale === 'es' ? category.labelEs : category.labelEn}</span>
            </button>
          ))}
        </div>
      )}
      
      {/* Search Input Container */}
      <div className="relative">
        <div className="relative">
          {/* Search Icon */}
          {showIcon && (
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
              <Search className={cn(safeAccess(iconSizes, size, iconSizes.md), 'text-gray-400')} />
            </div>
          )}
          
          {/* Input */}
          <input
            ref={inputRef}
            type="text"
            value={internalValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={displayPlaceholder}
            disabled={disabled}
            required={required}
            className={cn(
              'w-full rounded-md transition-colors duration-200',
              'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50',
              safeAccess(sizeClasses, size, sizeClasses.md),
              safeAccess(variantClasses, variant, variantClasses.default),
              showIcon && 'pl-10',
              internalValue && 'pr-10',
              isFocused ? 'border-primary-500' : 'border-gray-300',
              disabled && 'cursor-not-allowed bg-gray-50 text-gray-400'
            )}
            {...props}
          />
          
          {/* Clear Button */}
          {internalValue && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className={safeAccess(iconSizes, size, iconSizes.md)} />
            </button>
          )}
        </div>
        
        {/* Dropdown */}
        {showDropdown && (isFocused || shouldShowSuggestions || shouldShowRecentSearches) && (
          <div
            ref={dropdownRef}
            className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-y-auto"
          >
            {/* Loading State */}
            {loadingSuggestions && (
              <div className="p-3 text-center text-sm text-gray-500">
                <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                <span className="ml-2">
                  {locale === 'es' ? 'Buscando...' : 'Searching...'}
                </span>
              </div>
            )}
            
            {/* Suggestions */}
            {shouldShowSuggestions && !loadingSuggestions && (
              <div>
                <div className="p-2 border-b border-gray-100">
                  <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {locale === 'es' ? 'Sugerencias' : 'Suggestions'}
                  </h4>
                </div>
                {filteredSuggestions().map((suggestion) => (
                  <button
                    key={suggestion.id}
                    type="button"
                    onClick={() => handleSuggestionSelect(suggestion)}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between"
                  >
                    <div className="flex items-center space-x-2">
                      {suggestion.icon && (
                        <span className="text-gray-400">
                          {suggestion.icon}
                        </span>
                      )}
                      <span>{suggestion.text}</span>
                    </div>
                    {suggestion.metadata && (
                      <span className="text-xs text-gray-400">
                        {suggestion.metadata}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
            
            {/* Recent Searches */}
            {shouldShowRecentSearches && !loadingSuggestions && (
              <div>
                <div className="p-2 border-b border-gray-100 flex items-center justify-between">
                  <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {locale === 'es' ? 'Búsquedas recientes' : 'Recent searches'}
                  </h4>
                  {internalRecentSearches.length > 0 && (
                    <button
                      type="button"
                      onClick={clearRecentSearches}
                      className="text-xs text-gray-400 hover:text-gray-600"
                    >
                      {locale === 'es' ? 'Limpiar' : 'Clear'}
                    </button>
                  )}
                </div>
                {internalRecentSearches.map((recentSearch, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleRecentSearchSelect(recentSearch)}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2"
                  >
                    <Clock className="h-3 w-3 text-gray-400" />
                    <span>{recentSearch}</span>
                  </button>
                ))}
              </div>
            )}
            
            {/* No Results */}
            {!loadingSuggestions && 
             !shouldShowSuggestions && 
             !shouldShowRecentSearches &&
             internalValue.length >= minQueryLength && (
              <div className="p-3 text-center text-sm text-gray-500">
                {locale === 'es' ? 'No se encontraron resultados' : 'No results found'}
              </div>
            )}
            
            {/* Hint */}
            {!loadingSuggestions && 
             internalValue.length > 0 && 
             internalValue.length < minQueryLength && (
              <div className="p-3 text-center text-xs text-gray-400">
                {locale === 'es' 
                  ? `Escribe al menos ${minQueryLength} caracteres para buscar`
                  : `Type at least ${minQueryLength} characters to search`
                }
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Undo Notification */}
      {undoState.isActive && (
        <div className="mt-2 bg-gray-800 text-white text-sm rounded-md p-3 flex items-center justify-between">
          <span>
            {locale === 'es' ? 'Historial de búsqueda modificado' : 'Search history modified'}
          </span>
          <button
            type="button"
            onClick={handleUndo}
            className="flex items-center space-x-1 text-primary-200 hover:text-primary-100 font-medium"
          >
            <RotateCcw className="h-3 w-3" />
            <span>{locale === 'es' ? 'Deshacer' : 'Undo'}</span>
          </button>
        </div>
      )}
    </div>
  )
})

BirhausSearchInput.displayName = 'BirhausSearchInput'

// Hook for enhanced search functionality
export const useBirhausSearch = (initialQuery: string = '') => {
  const [query, setQuery] = useState(initialQuery)
  const [recentSearches, setRecentSearches] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([])
  
  const addToHistory = useCallback((searchQuery: string) => {
    if (!searchQuery.trim()) return
    
    setRecentSearches(prev => {
      const newHistory = [searchQuery, ...prev.filter(item => item !== searchQuery)]
      return newHistory.slice(0, 5) // Keep only 5 recent searches
    })
  }, [])
  
  const clearHistory = useCallback(() => {
    setRecentSearches([])
  }, [])
  
  const performSearch = useCallback(async (
    searchQuery: string,
    searchFn?: (query: string) => Promise<SearchSuggestion[]>
  ) => {
    if (!searchQuery.trim()) return
    
    setIsLoading(true)
    addToHistory(searchQuery)
    
    if (searchFn) {
      try {
        const results = await searchFn(searchQuery)
        setSuggestions(results)
      } catch (error) {
        console.error('Search error:', error)
        setSuggestions([])
      }
    }
    
    setIsLoading(false)
  }, [addToHistory])
  
  return {
    query,
    setQuery,
    recentSearches,
    setRecentSearches,
    addToHistory,
    clearHistory,
    isLoading,
    setIsLoading,
    suggestions,
    setSuggestions,
    performSearch
  }
}

// IPU Admin specific search suggestions
export const ipuAdminSearchCategories: SearchCategory[] = [
  {
    id: 'members',
    labelEs: 'Miembros',
    labelEn: 'Members',
    icon: <Users className="h-4 w-4" />,
    color: 'blue'
  },
  {
    id: 'donations',
    labelEs: 'Donaciones',
    labelEn: 'Donations',
    icon: <DollarSign className="h-4 w-4" />,
    color: 'green'
  },
  {
    id: 'events',
    labelEs: 'Eventos',
    labelEn: 'Events',
    icon: <Calendar className="h-4 w-4" />,
    color: 'purple'
  },
  {
    id: 'reports',
    labelEs: 'Reportes',
    labelEn: 'Reports',
    icon: <TrendingUp className="h-4 w-4" />,
    color: 'orange'
  }
]

// Helper function to create search suggestions for IPU Admin
export const createIPUAdminSuggestion = (
  id: string,
  text: string,
  category: SearchCategory,
  metadata?: string
): SearchSuggestion => ({
  id,
  text,
  category,
  icon: category.icon,
  metadata
})